import { DecimalPipe } from '@angular/common';
import { Component, effect, inject, input, output, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CropField } from '@next/arable-lib/crop-field';

import { UnsavedChangesService } from '../../../../../../../shared/services/unsaved-changes.service';
import { ProcessedAnswers, ProcessedAnswersWithForm } from '../../../../../../shared/models/models';
import { TgaInputType, TGAQuestion, TGATableQuestion } from '../../../../models/question-models';
import { updateDisabledQuestions, updateVisibilityOnDependantQuestions } from '../../../question-helpers';
import { SpecificFormHeaderSectionComponent } from '../../../../form-header-section/specific-form-header-section/specific-form-header-section.component';
import { QuestionsListFormComponent } from '../../../questions-list-form/questions-list-form.component';

@Component({
  selector: 'app-specific-questionnaire',
  templateUrl: './specific-questionnaire.component.html',
  providers: [DecimalPipe],
  imports: [SpecificFormHeaderSectionComponent, QuestionsListFormComponent, FormsModule, ReactiveFormsModule],
})
export class SpecificQuestionnaireComponent {
  title = input.required<string>();
  questions = input.required<TGAQuestion<TgaInputType>[]>();
  cropField = input<CropField>();
  cropYear = input.required<number | null>();
  readOnly = input(false);
  formStateUpdater = input<((questions: TGAQuestion<TgaInputType>[], formGroup: FormGroup) => void) | undefined>(
    undefined,
  );
  answersUpdated = output<ProcessedAnswersWithForm>();

  private readonly unsavedChangesService = inject(UnsavedChangesService);

  formGroup: FormGroup = new FormGroup({});
  formReady = signal(false);

  constructor() {
    effect(() => {
      const questions = this.questions();
      this.buildForm(questions);
    });

    effect(() => {
      const questions = this.questions();
      if (!this.formReady()) return;

      if (this.readOnly()) {
        this.formGroup.disable({ emitEvent: false });
      } else {
        this.formGroup.enable({ emitEvent: false });
        updateDisabledQuestions(questions, this.formGroup);
        this.formStateUpdater()?.(questions, this.formGroup);
      }
    });
  }

  emitAnswersUpdated(formGroup: FormGroup): void {
    if (this.readOnly()) return;

    const formValues = formGroup.value;

    this.markFormGroupTouched(formGroup);

    if (formGroup.valid) {
      const processedAnswers = this.processFormAnswers(formValues);

      this.unsavedChangesService.registerForm(formGroup);

      this.answersUpdated.emit({
        answers: processedAnswers,
        formGroup: formGroup,
      });
    }
  }

  private buildForm(questions: TGAQuestion<TgaInputType>[]): void {
    if (questions.length === 0) {
      return;
    }

    const group: { [key: string]: AbstractControl } = {};

    const addQuestionToForm = (question: TGAQuestion<TgaInputType>) => {
      let initialValue: TgaInputType | Date | undefined = question.value;

      // Convert year number to Date object for date-year questions using UTC to avoid timezone shifts
      if (question.controlType === 'date-year' && typeof question.value === 'number') {
        initialValue = new Date(Date.UTC(question.value, 0, 1));
      }

      const control = new FormControl(initialValue);
      this.addQuestionValidators(question, control);
      group[question.key] = control;
    };

    for (const question of questions) {
      if (question.controlType === 'table') {
        // For table questions, we don't add the table itself to the form
        // but we add all its sub-questions
        const tableQuestion = question as TGATableQuestion;
        for (const subQuestion of tableQuestion.subQuestions) {
          addQuestionToForm(subQuestion);
        }
      } else {
        addQuestionToForm(question);
      }
    }

    this.formGroup = new FormGroup(group);
    updateVisibilityOnDependantQuestions(this.questions(), this.formGroup);
    updateDisabledQuestions(this.questions(), this.formGroup);
    this.formStateUpdater()?.(questions, this.formGroup);

    this.unsavedChangesService.registerForm(this.formGroup);

    this.formReady.set(true);
  }

  private addQuestionValidators(question: TGAQuestion<TgaInputType>, control: AbstractControl): void {
    if (question.required) {
      control.addValidators(Validators.required);
    }
  }

  private processFormAnswers(formValues: Record<string, unknown>): ProcessedAnswers {
    const processedAnswers: ProcessedAnswers = {};

    for (const question of this.questions()) {
      if (question.controlType === 'table') {
        // For table questions, process all sub-questions
        const tableQuestion = question as TGATableQuestion;
        for (const subQuestion of tableQuestion.subQuestions) {
          const subValue = formValues[subQuestion.key];

          switch (subQuestion.controlType) {
            case 'dropdown':
            case 'radio-button': {
              processedAnswers[subQuestion.key] =
                subValue !== null && subValue !== undefined ? (subValue as string) : '';
              break;
            }
            default: {
              processedAnswers[subQuestion.key] =
                subValue !== null && subValue !== undefined ? (subValue as string) : '';
            }
          }
        }
      } else {
        const value = formValues[question.key];

        switch (question.controlType) {
          case 'dropdown':
          case 'radio-button': {
            processedAnswers[question.key] = value !== null && value !== undefined ? (value as string) : '';
            break;
          }
          default: {
            processedAnswers[question.key] = value !== null && value !== undefined ? (value as string) : '';
          }
        }
      }
    }

    return processedAnswers;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    for (const key of Object.keys(formGroup.controls)) {
      const control = formGroup.get(key);
      if (control) {
        control.markAsTouched();

        if (control instanceof FormGroup) {
          this.markFormGroupTouched(control);
        }
      }
    }
  }
}
